import React from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, TextField } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import type { ReferenceLabelValue } from '@/types/referenceLabels';

interface ReferenceLabelPickerProps {
  channelId: number;
  pathogenName: string;
  value?: ReferenceLabelValue | null;
  note?: string;
  concentration?: number;
  onChange: (label: ReferenceLabelValue | null) => void;
  onNoteChange?: (note: string) => void;
  onNoteSave?: (note: string) => void;
  onConcentrationChange?: (value: number | undefined) => void;
  onConcentrationSave?: () => void;
  disabled?: boolean;
}

export const ReferenceLabelPicker: React.FC<ReferenceLabelPickerProps> = ({
  channelId,
  pathogenName,
  value,
  note,
  concentration,
  onChange,
  onNoteChange,
  onNoteSave,
  onConcentrationChange,
  onConcentrationSave,
  disabled,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* Row 1: channel label + toggle buttons */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ minWidth: 100, flexShrink: 0, fontWeight: 500 }}>
          CH{channelId}: {pathogenName}
        </Typography>
        <ToggleButtonGroup
          value={value ?? null}
          exclusive
          onChange={(_e, val) => onChange(val as ReferenceLabelValue | null)}
          disabled={disabled}
          size="small"
          aria-label={`Reference label for ${pathogenName}`}
        >
          <ToggleButton
            value="positive"
            aria-label="Positive"
            sx={{
              '&.Mui-selected': { color: 'error.main', bgcolor: 'error.light', '&:hover': { bgcolor: 'error.light' } },
            }}
          >
            <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
            Positive
          </ToggleButton>
          <ToggleButton
            value="negative"
            aria-label="Negative"
            sx={{
              '&.Mui-selected': { color: 'success.dark', bgcolor: 'success.light', '&:hover': { bgcolor: 'success.light' } },
            }}
          >
            <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
            Negative
          </ToggleButton>
          <ToggleButton
            value="rejected"
            aria-label="Rejected"
            sx={{
              '&.Mui-selected': { color: 'warning.dark', bgcolor: 'warning.light', '&:hover': { bgcolor: 'warning.light' } },
            }}
          >
            <BlockIcon fontSize="small" sx={{ mr: 0.5 }} />
            Rejected
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Row 2: concentration input + notes input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: '108px' }}>
        <TextField
          type="number"
          size="small"
          sx={{ width: 140 }}
          placeholder="cp/mL"
          value={concentration ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            onConcentrationChange?.(raw === '' ? undefined : Number(raw));
          }}
          onBlur={() => onConcentrationSave?.()}
          disabled={disabled}
          inputProps={{
            min: 0,
            step: 'any',
            'aria-label': `Concentration for CH${channelId}`,
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            },
          }}
        />
        <TextField
          size="small"
          sx={{ flex: 1 }}
          placeholder="Note…"
          value={note ?? ''}
          onChange={(e) => onNoteChange?.(e.target.value)}
          onBlur={() => onNoteSave?.(note ?? '')}
          disabled={disabled}
          inputProps={{
            'aria-label': `Note for CH${channelId}`,
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            },
          }}
        />
      </Box>
    </Box>
  );
};
